import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TextField,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import api from '../../utils/api';

const riskStyles = {
  HIGH: { label: 'High', color: 'error', bg: '#fee2e2' },
  MEDIUM: { label: 'Medium', color: 'warning', bg: '#ffedd5' },
  LOW: { label: 'Low', color: 'success', bg: '#dcfce7' },
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

const FallReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [riskFilter, setRiskFilter] = useState('');
  const [search, setSearch] = useState('');

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await api.get('/fall-reports/', {
        params: {
          risk: riskFilter || undefined,
          search: search.trim() || undefined,
        },
      });
      setReports(response.data || []);
    } catch (error) {
      console.error('Failed to load fall reports', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskFilter, search]);

  const totalMonthFalls = useMemo(
    () => reports.reduce((acc, item) => acc + (item.fall_count_month || 0), 0),
    [reports]
  );

  const handleSendReport = async (clientId) => {
    if (!clientId) return;
    try {
      setSendingId(clientId);
      await api.post('/fall-reports/send/', { client_id: clientId });
    } catch (error) {
      console.error('Failed to send report', error);
    } finally {
      setSendingId(null);
    }
  };

  return (
    <Container sx={{ mt: 5 }}>
      <Typography variant="h4" gutterBottom>
        Fall Reports
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Monitor current-month fall activity for non-staff users and prioritize follow-ups by risk.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Risk</InputLabel>
            <Select
              label="Risk"
              value={riskFilter}
              onChange={(e) => setRiskFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="LOW">Low</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Search name, email, username"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
          />
          <Box>
            <Typography variant="overline" color="text.secondary">
              Total falls this month
            </Typography>
            <Typography variant="h5">{totalMonthFalls}</Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Falls (Month)</TableCell>
                    <TableCell>Risk</TableCell>
                    <TableCell>Last Fall</TableCell>
                    <TableCell>Assigned Staff</TableCell>
                    <TableCell>Report</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((row) => {
                    const style = riskStyles[row.risk] || riskStyles.LOW;
                    const latestAssigned = row.recent_falls?.[0]?.assigned_user || '-';
                    return (
                      <TableRow key={row.user_id}>
                        <TableCell>
                          <Typography variant="subtitle2">
                            {row.name || row.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.username}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.email || '-'}</TableCell>
                        <TableCell>{row.fall_count_month}</TableCell>
                        <TableCell>
                          <Chip
                            label={style.label}
                            color={style.color}
                            size="small"
                            sx={{ backgroundColor: style.bg }}
                          />
                        </TableCell>
                        <TableCell>{formatDateTime(row.last_fall_date)}</TableCell>
                        <TableCell>{latestAssigned}</TableCell>
                        <TableCell>
                          <Chip
                            label={sendingId === row.client_id ? 'Sending...' : 'Send report'}
                            color="primary"
                            size="small"
                            onClick={() => handleSendReport(row.client_id)}
                            sx={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {reports.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="text.secondary">No fall reports found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <Box px={3} py={2} display="flex" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                Showing non-staff users only.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Current month window.
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default FallReportsPage;
